import React, {useEffect, useRef, useState} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import type {
  CourseSessionContentResponse,
  CourseSessionCurrentContentResponse,
  CourseSessionQuizResponse,
  CourseSessionTrackerResponse,
} from 'src/api/ProactApiClient';
import {CourseDefinitionContentTypeEnum} from 'src/api/ProactApiClient';
import {
  useUpdateSessionContentTimeoutMutation,
  useGetCurrentSessionContentQuery,
  useUpdateCurrentSessionLessonContentMutation,
  useCloseCurrentSessionLessonContentMutation,
  useCloseCurrentSessionQuizContentMutation,
} from 'src/redux/queries/courseSession';
import {Spinner} from 'src/design/atoms/Spinner';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useCountdownTimer} from 'src/shared/hooks/useCountdownTimer';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {TrackerActiveSessionDemonstration} from './TrackerActiveSessionDemonstration';
import {TrackerActiveSessionLesson} from './TrackerActiveSessionLesson';
import {TrackerActiveSessionQuiz} from './TrackerActiveSessionQuiz';
import {TrackerActiveSessionQuizResults} from './TrackerActiveSessionQuizResults';
import {TrackerActiveSessionTimeout} from './TrackerActiveSessionTimeout';
import {TrackerActiveSessionTransition} from './TrackerActiveSessionTransition';
import {MapQuizResponseToQuizRequest} from './sessionHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

export interface TrackerActiveSessionProps {
  tracker: CourseSessionTrackerResponse;
  onHoldChange?: (held: boolean) => void;
}
export const TrackerActiveSession: React.FC<TrackerActiveSessionProps> = ({
  tracker,
  onHoldChange,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const {session} = tracker;
  const courseOverviewPath = PathUtility.TrainingCourseOverview(
    String(tracker.assignment.id)
  );
  const {goBack, originIs} = useBackNavigation(courseOverviewPath);

  const exitTo = (defaultPath: string) => {
    if (originIs(PathUtility.DashboardRoots())) {
      goBack();
    } else {
      navigate(defaultPath);
    }
  };

  const [currentContent, setCurrentContent] = useState<
    CourseSessionCurrentContentResponse | undefined
  >(undefined);

  // If we have quiz results, we will always show the quiz results screen.
  const [quizResults, setQuizResults] = useState<
    CourseSessionQuizResponse | undefined
  >(undefined);
  const [quizContent, setQuizContent] = useState<
    CourseSessionCurrentContentResponse | undefined
  >(undefined);
  const showQuizResultsScreen = quizResults !== undefined;

  const [updateSessionContentTimeout] =
    useUpdateSessionContentTimeoutMutation();
  const [updateCurrentSessionLessonContent] =
    useUpdateCurrentSessionLessonContentMutation();
  const [closeCurrentSessionLessonContent] =
    useCloseCurrentSessionLessonContentMutation();
  const [closeCurrentSessionQuizContent] =
    useCloseCurrentSessionQuizContentMutation();

  const hasInitialized = useRef(false);
  const [isSubmitPending, setIsSubmitPending] = useState(false);
  const [isContentLoaded, setIsContentLoaded] = useState(false);
  const isLoading = !isContentLoaded || isSubmitPending;

  const [showTransitionScreen, setShowTransitionScreen] = useState(false);

  const isHeld = showQuizResultsScreen || showTransitionScreen;
  useEffect(() => {
    onHoldChange?.(isHeld);
  }, [isHeld, onHoldChange]);

  const baseTimeLimitAt = currentContent?.contentSession?.timeLimitAt;
  const [timeLimit, setTimeLimit] = useState(baseTimeLimitAt);
  const isTimerEnabled =
    baseTimeLimitAt !== undefined && baseTimeLimitAt !== null;

  const [timeLimitBreach, setTimeLimitBreach] = useState(false);
  const isTimerActive = isTimerEnabled && timeLimit !== undefined;
  const [days, hours, minutes, seconds] = useCountdownTimer(timeLimit);
  const timeLimitNearBreach = days <= 0 && hours <= 0 && minutes <= 5;
  const timeLimitColor = timeLimitNearBreach === true ? 'red' : 'yellow';
  const [isTimeLimitTimeoutPending, setIsTimeLimitTimeoutPending] =
    useState(false);

  useEffect(() => {
    if (
      isLoading === true ||
      isTimeLimitTimeoutPending === true ||
      isTimerActive !== true
    ) {
      return;
    }

    if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
      console.log('Attempting timeout');
      setIsTimeLimitTimeoutPending(true);
      void handleTimeout();
    }
  }, [days, hours, minutes, seconds]);

  const handleTimeout = async () => {
    console.log('Processing timeout');

    if (currentContent === undefined) {
      console.error(
        'Current Content should not be undefined when timing out content.'
      );
      return;
    }

    // Countdown reaching zero is itself the breach signal — update UI immediately
    // so the user sees the timeout screen without waiting for the server round-trip.
    setTimeLimitBreach(true);

    try {
      await updateSessionContentTimeout({
        sessionId: currentContent.sessionId!,
        sessionContentId: currentContent.contentSession!.id!,
        type: CourseDefinitionContentTypeEnum.Invalid,
      }).unwrap();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const sessionId = session?.id;
  const {
    data: initialContent,
    isLoading: isInitialContentLoading,
    error: initialContentError,
  } = useGetCurrentSessionContentQuery(sessionId!, {
    skip: sessionId === undefined,
    refetchOnMountOrArgChange: true,
  });

  // One-way ratchet: if the server reports timeLimitBreached at any point (initial load
  // or any subsequent refetch), lock the breach state locally. Never resets to false —
  // component remount is the only reset path.
  useEffect(() => {
    if (initialContent?.timeLimitBreached === true) {
      setTimeLimitBreach(true);
    }
  }, [initialContent]);

  // When initial content loads, process side effects (navigate or set state).
  // This effect runs on every change of initialContent, including cache-invalidation
  // refetches triggered by mutations. The hasInitialized guard ensures that once the
  // component has performed its first-load logic, subsequent refetches (e.g. after quiz
  // submit) cannot re-trigger navigation or re-initialization. The ratchet effect above
  // (lines 129-133) intentionally runs on every refetch and is left separate.
  useEffect(() => {
    if (isInitialContentLoading) return;
    if (initialContentError) {
      setAlert(parseErrorMessage(initialContentError), 'error');
      setIsContentLoaded(true);
      // Intentionally do NOT set hasInitialized here so a subsequent successful
      // refetch can still perform first-load initialization.
      return;
    }
    if (!initialContent) return;

    // Once initialized, skip navigate/initialize logic for all subsequent refetches.
    if (hasInitialized.current) {
      return;
    }

    if (initialContent.hasContentRemaining === false) {
      hasInitialized.current = true;
      navigate(
        PathUtility.TrainingSessionRecord(String(sessionId), 'overview')
      );
    } else if (initialContent.timeLimitBreached === true) {
      hasInitialized.current = true;
      setTimeLimitBreach(true);
    } else {
      hasInitialized.current = true;
      setCurrentContent(initialContent);
      setTimeLimit(initialContent.contentSession!.timeLimitAt!);
    }
    setIsContentLoaded(true);
  }, [isInitialContentLoading, initialContent, initialContentError]);

  async function handleLessonVideoTimeEventUpdate(
    seconds: number
  ): Promise<void> {
    try {
      if (currentContent === undefined) {
        console.error(
          'Current Content should not be undefined when logging video time.'
        );
        return;
      }

      await updateCurrentSessionLessonContent({
        sessionId: currentContent.sessionId!,
        sessionContentId: currentContent.contentSession!.id!,
        type: CourseDefinitionContentTypeEnum.Lesson,
        currentProgressSeconds: seconds,
      }).unwrap();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }

  async function handleLessonVideoEnd(seconds: number): Promise<void> {
    try {
      if (currentContent === undefined) {
        console.error(
          'Current Content should not be undefined when closing video session.'
        );
        return;
      }

      const res = await closeCurrentSessionLessonContent({
        sessionId: currentContent.sessionId!,
        sessionContentId: currentContent.contentSession!.id!,
        type: CourseDefinitionContentTypeEnum.Lesson,
        currentProgressSeconds: seconds,
      }).unwrap();

      if (res.timeLimitBreached === true) {
        setTimeLimitBreach(true);
      } else {
        onHoldChange?.(true);
        setShowTransitionScreen(true);
        setCurrentContent(res);
        setTimeLimit(res.contentSession!.timeLimitAt!);
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }

  function handleSessionContentChange(
    sessionContent: CourseSessionContentResponse
  ): void {
    setCurrentContent({
      ...currentContent,
      contentSession: sessionContent,
    });
  }

  async function handleQuizSubmit(): Promise<void> {
    try {
      if (currentContent === undefined) {
        console.error(
          'Current Content should not be undefined when closing quiz session.'
        );
        return;
      }

      setIsSubmitPending(true);

      const res = await closeCurrentSessionQuizContent({
        sessionId: currentContent.sessionId!,
        sessionContentId: currentContent.contentSession!.id!,
        type: CourseDefinitionContentTypeEnum.Lesson,
        quizSubmission: MapQuizResponseToQuizRequest(
          currentContent.contentSession!.quiz
        ),
      }).unwrap();

      if (res.nextContent?.timeLimitBreached === true) {
        setTimeLimitBreach(true);
      } else {
        onHoldChange?.(true);
        setShowTransitionScreen(true);
        setQuizContent(currentContent);
        setQuizResults(res.quizResults);
        setCurrentContent(res.nextContent);
        setTimeLimit(res.nextContent?.contentSession?.timeLimitAt);
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSubmitPending(false);
  }

  function handleNavigateToCourseOverview() {
    exitTo(courseOverviewPath);
  }

  return isLoading ? (
    <Spinner loadingText="Loading...." />
  ) : timeLimitBreach === true ? (
    <TrackerActiveSessionTimeout onContinue={handleNavigateToCourseOverview} />
  ) : (
    <Box>
      {isTimerActive === true && (
        <Alert
          mb="md"
          color={
            timeLimitColor
          }>{`Time remaining: ${hours} hours, ${minutes} minutes, and ${seconds} seconds.`}</Alert>
      )}
      {showQuizResultsScreen === true && quizResults && quizContent ? (
        <TrackerActiveSessionQuizResults
          quizContent={quizContent}
          quizResults={quizResults}
          onContinue={() => {
            setQuizContent(undefined);
            setQuizResults(undefined);
          }}
          onExit={() => exitTo(PathUtility.TrainingDashboard('user'))}
        />
      ) : showTransitionScreen === true ? (
        <TrackerActiveSessionTransition
          hasContentRemaining={currentContent?.hasContentRemaining}
          onContinue={() => setShowTransitionScreen(false)}
          onComplete={handleNavigateToCourseOverview}
        />
      ) : (
        <Box>
          {currentContent?.contentDefinition?.type ===
            CourseDefinitionContentTypeEnum.Lesson && (
            <TrackerActiveSessionLesson
              contentResponse={currentContent}
              onVideoEnd={handleLessonVideoEnd}
              onVideoTimeEventUpdate={handleLessonVideoTimeEventUpdate}
            />
          )}
          {currentContent?.contentDefinition?.type ===
            CourseDefinitionContentTypeEnum.Quiz && (
            <TrackerActiveSessionQuiz
              contentResponse={currentContent}
              onSessionContentChange={handleSessionContentChange}
              onSubmit={handleQuizSubmit}
            />
          )}
          {currentContent?.contentDefinition?.type ===
            CourseDefinitionContentTypeEnum.Demonstration && (
            <TrackerActiveSessionDemonstration
              contentResponse={currentContent}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
